import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Tag } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import { PromoCard } from "@/components/common";
import { HERO_SLIDES, PROMO_TRIO, ADJUSTABLE_PROMOS, SITE } from "@/mock";

const slug = (src) => src.split("/").pop().replace(/\.(webp|jpg|png)$/, "");

function Banner({ s, featured, wide }) {
  return (
    <Link
      to={s.to}
      aria-label={s.alt}
      data-testid={`promotion-banner-${slug(s.image)}`}
      className={`group block overflow-hidden rounded-2xl border border-neutral-200 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl ${featured || wide ? "md:col-span-2" : ""}`}
      style={{ backgroundColor: s.bg || "#000" }}
    >
      <img
        src={s.image}
        alt={s.alt}
        loading={featured ? "eager" : "lazy"}
        className={`${featured || wide ? "" : "aspect-[1000/405]"} h-auto w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]`}
      />
    </Link>
  );
}

function SectionTitle({ eyebrow, title }) {
  return (
    <div className="mb-6">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D0021B]">{eyebrow}</p>
      <h2 className="mt-1 text-2xl font-bold uppercase tracking-tight text-neutral-900 sm:text-3xl">{title}</h2>
    </div>
  );
}

export default function Promotions() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Promotions", path: "/promotions" },
  ];
  const [featured, ...rest] = HERO_SLIDES;
  return (
    <>
      <Seo
        title="Mattress Sales & Promotions - MattressTown Fort Lauderdale"
        description="Current mattress sales at MattressTown in Fort Lauderdale. Save up to $1,000 on Beautyrest Black, Serta, World Class, King Koil and adjustable mattress sets."
        path="/promotions"
        jsonLd={breadcrumbJsonLd(crumbs)}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8" data-testid="promotions-page">
        <header className="max-w-3xl">
          <p className="mb-2 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#D0021B]">
            <Tag className="h-4 w-4" aria-hidden="true" /> Fall Sale - Current Offers
          </p>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-neutral-900 sm:text-5xl" data-testid="promotions-heading">
            Promotions
          </h1>
          <p className="mt-3 text-neutral-600">
            Every current MattressTown offer in one place. Tap any sale to shop the mattresses and sets it covers,
            then visit our Fort Lauderdale showroom or call for your best price.
          </p>
        </header>

        <section className="mt-8 sm:mt-10" aria-label="Featured sales">
          <SectionTitle eyebrow="Featured" title="Top Sale Events" />
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2" data-testid="promotions-banners">
            <Banner s={featured} featured />
            {rest.map((s, i) => (
              <Banner key={s.image} s={s} wide={rest.length % 2 === 1 && i === rest.length - 1} />
            ))}
          </div>
        </section>

        <section className="mt-12 sm:mt-16" aria-label="Brand savings">
          <SectionTitle eyebrow="By Brand" title="Brand Fall Savings" />
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4" data-testid="promotions-cards">
            {PROMO_TRIO.map((p) => (
              <Link
                key={p.image}
                to={p.to}
                aria-label={p.alt}
                data-testid={`promotion-card-${slug(p.image)}`}
                className="group block overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <img
                  src={p.image}
                  alt={p.alt}
                  loading="lazy"
                  className="aspect-[848/1264] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12 sm:mt-16" aria-label="Adjustable set savings" data-testid="promotions-adjustable">
          <SectionTitle eyebrow="Mattress + Adjustable Base" title="Adjustable Set Savings" />
          {ADJUSTABLE_PROMOS.map((item) => (
            <PromoCard key={item.brand} item={item} />
          ))}
        </section>

        <div className="mt-12 rounded-2xl bg-neutral-900 px-6 py-10 text-center sm:mt-16">
          <h2 className="text-2xl font-bold uppercase text-white sm:text-3xl">Questions about an offer?</h2>
          <p className="mx-auto mt-2 max-w-xl text-neutral-300">
            Call our Fort Lauderdale showroom and our family will help you save on the perfect mattress.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="promotions-call-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#a80016]"
            >
              Call {SITE.phone}
            </a>
            <Link
              to="/mattresses"
              data-testid="promotions-shop-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-white hover:text-neutral-900"
            >
              Shop Mattresses <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
